/**
 * useConversationList - Hook para carregar listagem de conversas server-side
 *
 * CHANGELOG:
 *   v1.3.1 - 2026-06-26 — Adiciona refetchOnMount: 'always' na lista e no count
 *            para corrigir delay ao voltar de outra rota (ex: Kanban). Sem isso,
 *            o cache (gcTime 5min) era servido como "fresh" por até 30s
 *            (staleTime), e eventos Realtime perdidos durante a ausência da
 *            página nunca apareciam até o próximo evento. Mantém staleTime
 *            inalterado para preservar comportamento dentro da página.
 *   v1.3.0 - 2026-06-26 — Realtime agora usa patch otimista in-cache
 *            (unread/preview/last_message_at + reorder to top) para o caso
 *            comum (mensagem nova em conversa já visível), eliminando os
 *            refetches de get_conversations_list/get_conversations_list_count
 *            a cada evento. Refetch debounced (1500ms) só ocorre em mudanças
 *            estruturais (status/assigned_to/department_id/kanban_phase_id/
 *            inbox_id, INSERT, DELETE) ou quando o item não está em cache.
 *            Throttle de visibilitychange (>30s oculto) evita refetch em
 *            cada Alt-Tab. Mesmo padrão do useKanbanConversationRealtime.
 *   v1.0.0 - 2026-02-21 — Versão inicial server-side com RBAC, paginação
 *            cursor-based e filtros no SQL (substitui lógica client-side).
 */


import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useCallback, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { useTenantInboxIds, isFromCurrentTenant } from '@/hooks/useTenantInboxIds';
import type { Conversation, Contact, ChatGroup } from '@/pages/tenant/Chat';
import type { Database } from '@/integrations/supabase/types';

// Logger para depuração condicional
const logger = createLogger('ConversationList');

type ConversationStatus = Database['public']['Enums']['conversation_status'];

/** Interface de filtros aceitos pelo hook */
export interface ConversationListFilters {
  /** Multi-select de status (ex: ['pending', 'open']) ou ['all'] para todos */
  statusFilters?: (ConversationStatus | 'all')[];
  /** ID da inbox para filtrar */
  inboxId?: string | null;
  /** ID do departamento ou 'none' para sem departamento */
  departmentId?: string | null;
  /** ID do agente atribuído */
  agentId?: string | null;
  /** ID da tag do contato */
  tagId?: string | null;
  /** ID da fase kanban */
  phaseId?: string | null;
  /** ID da categoria kanban */
  categoryId?: string | null;
  /** 'read', 'unread' ou null para todas */
  readStatus?: 'all' | 'read' | 'unread' | null;
  /** 'individual', 'group' ou null para todas */
  conversationType?: 'all' | 'individual' | 'group' | null;
  /** Texto de busca (nome, telefone, email, preview) */
  search?: string | null;
  /**
   * Escopo rápido (v1.1.0):
   * - 'all'  = todas as conversas visíveis (default)
   * - 'mine' = somente atribuídas ao usuário logado
   * - 'bot'  = somente conversas sem agente humano e com bot ativo (não pausado)
   */
  scope?: 'all' | 'mine' | 'bot' | null;
}

// Tamanho de página padrão
const PAGE_SIZE = 10; // carga inicial reduzida (25 -> 10) para acelerar o primeiro render

/**
 * Converte o retorno plano da RPC em um objeto Conversation tipado
 * Mapeia campos flat da RPC para a estrutura aninhada esperada pelo Chat.tsx
 */
function mapRpcToConversation(row: any, selectedConversationId?: string | null): Conversation {
  // Parse das tags do contato (vem como JSONB da RPC)
  const tags: Contact['tags'] = Array.isArray(row.contact_tags)
    ? row.contact_tags
    : (typeof row.contact_tags === 'string' ? JSON.parse(row.contact_tags) : row.contact_tags || []);

  // Monta objeto Contact (null para grupos)
  const contact: Contact | null = row.contact_id ? {
    id: row.contact_id,
    name: row.contact_name,
    phone: row.contact_phone,
    email: row.contact_email,
    avatar_url: row.contact_avatar_url,
    is_saved_to_agenda: row.contact_is_saved_to_agenda ?? false,
    metadata: row.contact_metadata as Record<string, unknown> | null,
    channel_ids: row.contact_channel_ids as Contact['channel_ids'],
    tags,
  } : null;

  // Monta objeto ChatGroup (null para individuais)
  const group: ChatGroup | null = row.group_id ? {
    id: row.group_id,
    name: row.group_name || '',
    description: row.group_description || null,
    avatar_url: row.group_avatar_url || null,
    participant_count: row.group_participant_count || 0,
    external_id: row.group_external_id || '',
    is_readonly: row.group_is_readonly || null,
    is_announcement: row.group_is_announcement || null,
    auto_distribution_enabled: row.group_auto_distribution_enabled || null,
  } : null;

  // Se é a conversa selecionada, força unread_count=0
  const isSelected = selectedConversationId === row.id;

  return {
    id: row.id,
    inbox_id: row.inbox_id,
    contact_id: row.contact_id,
    contact,
    group_id: row.group_id,
    group,
    inbox: {
      id: row.inbox_id,
      name: row.inbox_name || '',
      channel_type: row.inbox_channel_type || '',
      identifier: row.inbox_identifier || '',
      settings: row.inbox_settings as Conversation['inbox']['settings'],
      provider_slug: row.inbox_provider_slug || null,
    },
    assigned_to: row.assigned_to,
    assigned_agent: row.assigned_agent_name ? {
      id: row.assigned_to,
      full_name: row.assigned_agent_name,
      avatar_url: row.assigned_agent_avatar,
    } : undefined,
    department_id: row.department_id,
    department: row.department_name ? {
      id: row.department_id,
      name: row.department_name,
    } : null,
    kanban_phase_id: row.kanban_phase_id || null,
    kanban_phase: row.phase_name ? {
      id: row.kanban_phase_id,
      name: row.phase_name,
      color: row.phase_color || '',
      category: row.category_id ? {
        id: row.category_id,
        name: row.category_name || '',
        color: row.category_color || '',
      } : null,
    } : null,
    status: row.status as ConversationStatus,
    last_message_at: row.last_message_at,
    last_message_preview: row.last_message_preview,
    unread_count: isSelected ? 0 : row.unread_count,
    created_at: row.created_at,
    has_non_campaign_messages: row.has_non_campaign_messages ?? true,
    last_inbound_at: row.last_inbound_at || null,
    bot_paused: row.bot_paused || false,
    bot_paused_at: row.bot_paused_at || null,
    bot_paused_until: row.bot_paused_until || null,
    source_type: row.source_type || null,
    source_media_id: row.source_media_id || null,
    source_media_url: row.source_media_url || null,
    source_comment_id: row.source_comment_id || null,
    // v1.2.0: conversa fixada pelo usuário (vinda de conversation_pins via RPC)
    is_pinned: row.is_pinned ?? false,
  };
}

/**
 * Hook principal para listagem de conversas com paginação server-side
 * 
 * @param filters - Filtros da UI (status, inbox, busca, etc.)
 * @param selectedConversationId - ID da conversa selecionada (para zerar unread_count)
 * @returns Conversas, estados de loading, funções de paginação e invalidação
 */
export function useConversationList(
  filters: ConversationListFilters,
  selectedConversationId?: string | null
) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const inboxIdsRef = useTenantInboxIds();

  // Prepara parâmetros de filtro para a RPC
  const rpcFilters = {
    // Status: converte array ['all'] para null (sem filtro)
    p_status: filters.statusFilters?.includes('all') ? null : (filters.statusFilters as string[] || null),
    // Inbox
    p_inbox_id: (filters.inboxId && filters.inboxId !== 'all') ? filters.inboxId : null,
    // Departamento
    p_department_id: (filters.departmentId && filters.departmentId !== 'all') ? filters.departmentId : null,
    // Agente
    p_agent_id: (filters.agentId && filters.agentId !== 'all') ? filters.agentId : null,
    // Tag
    p_tag_id: (filters.tagId && filters.tagId !== 'all') ? filters.tagId : null,
    // Fase kanban
    p_phase_id: (filters.phaseId && filters.phaseId !== 'all') ? filters.phaseId : null,
    // Categoria kanban
    p_category_id: (filters.categoryId && filters.categoryId !== 'all') ? filters.categoryId : null,
    // Status de leitura
    p_read_status: (filters.readStatus && filters.readStatus !== 'all') ? filters.readStatus : null,
    // Tipo de conversa
    p_conversation_type: (filters.conversationType && filters.conversationType !== 'all') ? filters.conversationType : null,
    // Busca
    p_search: filters.search || null,
    // Escopo rápido Meus/Todos/Bot (v1.1.0). Default 'all' = sem filtro adicional.
    p_scope: filters.scope || 'all',
  };

  const query = useInfiniteQuery({
    // Query key inclui todos os filtros para cache granular
    queryKey: ['conversation-list', currentTenant?.id, rpcFilters],
    queryFn: async ({ pageParam }) => {
      logger.debug('Buscando conversas', { cursor: pageParam, filters: rpcFilters });

      // Chamada RPC v2.0.0 - SEM total_count (calculado por hook separado)
      const { data, error } = await (supabase.rpc as any)('get_conversations_list', {
        ...rpcFilters,
        p_limit: PAGE_SIZE,
        p_cursor_last_message_at: pageParam || null,
        p_tenant_hint: currentTenant?.id || null,
        p_include_total: false, // total agora vem de get_conversations_list_count
      });

      if (error) {
        logger.error('Erro ao buscar conversas:', error);
        throw error;
      }

      const rows = (data || []) as any[];
      logger.debug(`Recebidas ${rows.length} conversas`);

      // Mapeia retorno plano da RPC para objetos Conversation tipados
      const mapped = rows.map(row => mapRpcToConversation(row, selectedConversationId));
      return mapped;
    },
    getNextPageParam: (lastPage) => {
      // Se retornou menos que PAGE_SIZE, não há mais páginas
      if (lastPage.length < PAGE_SIZE) return undefined;
      // Cursor é o last_message_at do último item
      return lastPage[lastPage.length - 1]?.last_message_at;
    },
    initialPageParam: null as string | null,
    enabled: !!currentTenant?.id,
    staleTime: 30 * 1000, // 30 segundos - evita tempestade de refetch com muitas conversas
    gcTime: 5 * 60 * 1000, // 5 minutos
    // v1.3.1: ao remontar a página do Chat (volta de outra rota), força refetch
    // mesmo se cache ainda estiver "fresh". Evita exibir lista desatualizada
    // por eventos Realtime perdidos enquanto a Chat estava desmontada.
    refetchOnMount: 'always',
  });


  // =========================================================================
  // Hook separado para total_count - chamado com debounce de 500ms
  // Evita recalcular o COUNT(*) a cada paginação/refetch da lista principal
  // =========================================================================
  const [debouncedFilters, setDebouncedFilters] = useState(rpcFilters);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedFilters(rpcFilters), 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(rpcFilters)]);

  const countQuery = useQuery({
    queryKey: ['conversation-list-count', currentTenant?.id, debouncedFilters],
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as any)('get_conversations_list_count', {
        ...debouncedFilters,
        p_tenant_hint: currentTenant?.id || null,
      });
      if (error) {
        logger.error('Erro ao buscar total de conversas:', error);
        throw error;
      }
      return (data as number) ?? 0;
    },
    enabled: !!currentTenant?.id,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    // v1.3.1: igual à lista — força recálculo do total ao remontar a Chat
    refetchOnMount: 'always',
  });


  // =========================================================================
  // v1.3.0 — Realtime híbrido: patch otimista in-cache + debounce estrutural
  // Mesmo padrão validado em useKanbanConversationRealtime.
  // =========================================================================
  const listDirtyRef = useRef(false);
  const countDirtyRef = useRef(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastHiddenAtRef = useRef<number | null>(null);

  // Janela de coalescing (mesmo valor do Kanban)
  const DEBOUNCE_MS = 1500;
  // Throttle de visibilitychange: só invalida se ficou oculto > 30s
  const VISIBILITY_MIN_HIDDEN_MS = 30_000;

  useEffect(() => {
    if (!currentTenant?.id) return;

    logger.debug('Configurando Realtime híbrido para lista de conversas');

    /**
     * Aplica patch otimista em TODAS as queries em cache que casem o prefixo
     * ['conversation-list', tenantId] (varia conforme filtros).
     * Quando last_message_at fica mais recente, REMOVE da posição original e
     * REINSERE no topo da página 0 (mesma UX do Kanban).
     * Retorna true se o item foi encontrado em alguma página/filtro.
     */
    const patchConversationInCache = (
      conversationId: string,
      patch: Partial<Conversation>,
    ): boolean => {
      let patched = false;

      const queries = queryClient.getQueriesData<{ pages: Conversation[][]; pageParams: unknown[] }>({
        queryKey: ['conversation-list', currentTenant.id],
      });

      queries.forEach(([key, data]) => {
        if (!data?.pages?.length) return;

        let foundItem: Conversation | null = null;
        let foundPageIdx = -1;
        for (let i = 0; i < data.pages.length; i++) {
          const idx = data.pages[i].findIndex((c) => c.id === conversationId);
          if (idx >= 0) {
            foundItem = data.pages[i][idx];
            foundPageIdx = i;
            break;
          }
        }
        if (!foundItem) return;

        patched = true;

        // Respeita "conversa selecionada zera unread"
        const isSelected = selectedConversationId === conversationId;
        const merged: Conversation = {
          ...foundItem,
          ...patch,
          unread_count: isSelected ? 0 : (patch.unread_count ?? foundItem.unread_count),
        };

        const oldTs = foundItem.last_message_at ? Date.parse(foundItem.last_message_at) : 0;
        const newTs = merged.last_message_at ? Date.parse(merged.last_message_at) : 0;
        const shouldReorder = newTs > oldTs;

        if (!shouldReorder) {
          const newPages = data.pages.map((page) =>
            page.map((c) => (c.id === conversationId ? merged : c)),
          );
          queryClient.setQueryData(key, { ...data, pages: newPages });
          return;
        }

        const newPages = data.pages.map((page, i) => {
          const filtered = i === foundPageIdx ? page.filter((c) => c.id !== conversationId) : page;
          return i === 0 ? [merged, ...filtered] : filtered;
        });
        queryClient.setQueryData(key, { ...data, pages: newPages });
      });

      return patched;
    };

    /** Flush debounced: invalida lista e/ou count uma vez por janela. */
    const scheduleFlush = () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        if (listDirtyRef.current) {
          listDirtyRef.current = false;
          logger.debug('Flush debounced: invalidando lista de conversas');
          queryClient.invalidateQueries({
            queryKey: ['conversation-list', currentTenant.id],
          });
        }
        if (countDirtyRef.current) {
          countDirtyRef.current = false;
          queryClient.invalidateQueries({
            queryKey: ['conversation-list-count', currentTenant.id],
          });
        }
      }, DEBOUNCE_MS);
    };

    const markDirty = (list: boolean, count: boolean) => {
      if (list) listDirtyRef.current = true;
      if (count) countDirtyRef.current = true;
      scheduleFlush();
    };

    // Canal de conversations (escopado por tenant — v1.1.0 mantido)
    const conversationsChannel = supabase
      .channel(`conv-list-conversations-${currentTenant.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'conversations' },
        (payload) => {
          if (!isFromCurrentTenant(inboxIdsRef, payload)) return;

          // Descarta enquanto aba oculta — visibilitychange cuida do refresh
          if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
            return;
          }

          const eventType = payload.eventType;
          const oldRow = payload.old as any;
          const newRow = payload.new as any;

          if (eventType === 'INSERT' || eventType === 'DELETE') {
            // Estrutural: refetch lista + count
            markDirty(true, true);
            return;
          }

          // UPDATE — detecta mudanças estruturais que afetam pertencimento
          const structuralChanged =
            (oldRow?.status ?? null) !== (newRow?.status ?? null) ||
            (oldRow?.assigned_to ?? null) !== (newRow?.assigned_to ?? null) ||
            (oldRow?.department_id ?? null) !== (newRow?.department_id ?? null) ||
            (oldRow?.kanban_phase_id ?? null) !== (newRow?.kanban_phase_id ?? null) ||
            (oldRow?.inbox_id ?? null) !== (newRow?.inbox_id ?? null);

          if (structuralChanged) {
            markDirty(true, true);
            return;
          }

          // Caso comum: mensagem nova / unread / preview — patch otimista
          if (newRow?.id) {
            const patched = patchConversationInCache(newRow.id, {
              last_message_at: newRow.last_message_at ?? null,
              last_message_preview: newRow.last_message_preview ?? null,
              unread_count: newRow.unread_count ?? 0,
              status: newRow.status,
              bot_paused: newRow.bot_paused ?? false,
              bot_paused_at: newRow.bot_paused_at ?? null,
              bot_paused_until: newRow.bot_paused_until ?? null,
              last_inbound_at: newRow.last_inbound_at ?? null,
            });

            if (patched) {
              logger.debug('Patch otimista aplicado na lista', { conv_id: newRow.id });
            } else {
              // Item fora da janela paginada — refetch lista (sem count)
              markDirty(true, false);
            }
          }
        },
      )
      .subscribe();

    // Tags de contato — estrutural (pode alterar filtro por tag), só lista
    const tagsChannel = supabase
      .channel(`conv-list-tags-${currentTenant.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'contact_tag_assignments' },
        () => {
          if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
          markDirty(true, false);
        },
      )
      .subscribe();

    // Pinos do usuário — só lista (ordem muda); count não muda
    const pinsChannel = supabase
      .channel(`conv-list-pins-${currentTenant.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'conversation_pins' },
        () => {
          if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
          markDirty(true, false);
        },
      )
      .subscribe();

    // Throttle de visibilitychange (>30s oculto): evita refetch em cada Alt-Tab
    const handleVisibility = () => {
      if (typeof document === 'undefined') return;
      if (document.visibilityState === 'hidden') {
        lastHiddenAtRef.current = Date.now();
        return;
      }
      const hiddenAt = lastHiddenAtRef.current;
      lastHiddenAtRef.current = null;
      if (!hiddenAt) return;
      const hiddenFor = Date.now() - hiddenAt;
      if (hiddenFor < VISIBILITY_MIN_HIDDEN_MS) return;
      logger.debug(`Aba voltou após ${Math.round(hiddenFor / 1000)}s — invalidando lista + count`);
      queryClient.invalidateQueries({ queryKey: ['conversation-list', currentTenant.id] });
      queryClient.invalidateQueries({ queryKey: ['conversation-list-count', currentTenant.id] });
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      logger.debug('Removendo Realtime híbrido da lista de conversas');
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      document.removeEventListener('visibilitychange', handleVisibility);
      supabase.removeChannel(conversationsChannel);
      supabase.removeChannel(tagsChannel);
      supabase.removeChannel(pinsChannel);
    };
  }, [currentTenant?.id, queryClient, inboxIdsRef, selectedConversationId]);


  // Função para invalidar cache manualmente (lista + total)
  const invalidateList = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ['conversation-list', currentTenant?.id],
    });
    queryClient.invalidateQueries({
      queryKey: ['conversation-list-count', currentTenant?.id],
    });
  }, [queryClient, currentTenant?.id]);

  // ===========================================================================
  // v1.2.0: Mutations para fixar/desfixar conversa
  // - Persistência em conversation_pins (multi-dispositivo).
  // - onSuccess invalida a lista para que pinned migre ao topo na próxima query.
  // ===========================================================================
  const pinMutation = useMutation({
    mutationFn: async (conversationId: string) => {
      const { error } = await (supabase.rpc as any)('pin_conversation', {
        p_conversation_id: conversationId,
        p_tenant_hint: currentTenant?.id || null,
      });
      if (error) {
        logger.error('Erro ao fixar conversa:', error);
        throw error;
      }
    },
    onSuccess: () => {
      invalidateList();
    },
  });

  const unpinMutation = useMutation({
    mutationFn: async (conversationId: string) => {
      const { error } = await (supabase.rpc as any)('unpin_conversation', {
        p_conversation_id: conversationId,
        p_tenant_hint: currentTenant?.id || null,
      });
      if (error) {
        logger.error('Erro ao desfixar conversa:', error);
        throw error;
      }
    },
    onSuccess: () => {
      invalidateList();
    },
  });

  // Flatten de todas as páginas em um array único de conversas
  const conversations = query.data?.pages.flat() || [];

  // Total vem do hook separado get_conversations_list_count.
  // Fallback para o tamanho carregado enquanto o count ainda não chegou.
  const totalCount: number = countQuery.data ?? conversations.length;

  return {
    /** Lista de conversas (todas as páginas concatenadas) */
    conversations,
    /** Total real de conversas (via total_count da RPC, não apenas items carregados) */
    totalCount,
    /** Carregamento inicial */
    isLoading: query.isLoading,
    /** Buscando próxima página */
    isFetchingNextPage: query.isFetchingNextPage,
    /** Existe próxima página */
    hasNextPage: query.hasNextPage,
    /** Carrega próxima página (infinite scroll) */
    fetchNextPage: query.fetchNextPage,
    /** Invalida cache e força refetch */
    invalidateList,
    /** Erro da query */
    error: query.error,
    /** Refetch manual */
    refetch: query.refetch,
    /** v1.2.0: Fixa a conversa para o usuário logado */
    pinConversation: (id: string) => pinMutation.mutateAsync(id),
    /** v1.2.0: Desfixa a conversa do usuário logado */
    unpinConversation: (id: string) => unpinMutation.mutateAsync(id),
    /** v1.2.0: Estado da mutation pin/unpin (loading) */
    isPinning: pinMutation.isPending || unpinMutation.isPending,
  };
}
